import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { getApiBase } from "../apiBase";
import { isCvinExpansion } from "../projectGridColumns";
import { addExpansionSubsegment } from "./addProjectApi";
import { canAddExpansionSubsegment } from "./commercialRoleAccess";
import { filterExpansionRowsForBase, segmentFromSubsegmentPrefix } from "./expansionGroupUtils";
import {
  EXPANSION_DEPARTMENT_OPTIONS,
  EXPANSION_PHASE_OPTIONS,
  EXPANSION_STATUS_OPTIONS,
} from "./expansionProjectSchema";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import "./addProjectPage.css";

function FormField({ label, children, hint }) {
  return (
    <div className="add-project-field">
      <label>{label}</label>
      {children}
      {hint ? (
        <span className="project-detail-muted" style={{ fontSize: 12 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function CheckboxMultiSelect({ label, options, value, onChange, disabled, required }) {
  const selected = useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);
  return (
    <FormField label={label}>
      <div className="add-project-departments commercial-multiselect">
        {options.map((opt) => (
          <label key={opt} className="commercial-multiselect-option">
            <input
              type="checkbox"
              checked={selected.has(opt)}
              disabled={disabled}
              required={required && selected.size === 0}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(opt);
                else next.delete(opt);
                onChange([...next]);
              }}
            />
            {opt}
          </label>
        ))}
      </div>
    </FormField>
  );
}

function AddExpansionSubsegmentForm({
  apiUrl,
  getAuthHeaders,
  parentWorkOrder,
  segmentLabel,
  inheritedCustomer,
  nextWorkOrderHint,
}) {
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState("");
  const [workOrderOverride, setWorkOrderOverride] = useState("");
  const [segment, setSegment] = useState(segmentLabel || "");
  const [departments, setDepartments] = useState([...EXPANSION_DEPARTMENT_OPTIONS]);
  const [phase, setPhase] = useState("Drafting");
  const [status, setStatus] = useState("Active");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!projectName.trim()) {
      setError("Project name is required.");
      return;
    }
    if (!departments.length) {
      setError("Select at least one department.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        project_name: projectName.trim(),
        customer: inheritedCustomer,
        segment: segment.trim() || undefined,
        departments,
        phase,
        status,
      };
      const wo = workOrderOverride.trim();
      if (wo) payload.work_order = wo;

      const data = await addExpansionSubsegment(parentWorkOrder, payload, {
        apiUrl,
        getAuthHeaders,
      });
      const projectId = data?.project?.id;
      if (projectId != null) {
        navigate(`/project/${projectId}`);
        return;
      }
      navigate(`/expansion/segment/${parentWorkOrder}`);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not add subsegment.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="add-project-form" onSubmit={onSubmit}>
      <section className="add-project-section">
        <h3 className="add-project-section-title">Subsegment details</h3>
        <FormField
          label="Work order"
          hint={
            nextWorkOrderHint
              ? `Optional. Leave blank to use the next available suffix (e.g. ${nextWorkOrderHint}).`
              : "Optional. Leave blank to auto-assign the next suffix."
          }
        >
          <input
            type="text"
            value={workOrderOverride}
            disabled={saving}
            placeholder={nextWorkOrderHint || `${parentWorkOrder}.##`}
            onChange={(e) => setWorkOrderOverride(e.target.value)}
          />
        </FormField>
        <FormField label="Project name">
          <input
            type="text"
            value={projectName}
            disabled={saving}
            required
            placeholder="e.g. SEG 43 City of Example"
            onChange={(e) => setProjectName(e.target.value)}
          />
        </FormField>
        <FormField label="Segment" hint="Inherited from sibling subsegments when left blank.">
          <input
            type="text"
            value={segment}
            disabled={saving}
            placeholder={segmentLabel || "43"}
            onChange={(e) => setSegment(e.target.value)}
          />
        </FormField>
        <div className="add-project-row">
          <FormField label="Phase">
            <select value={phase} disabled={saving} onChange={(e) => setPhase(e.target.value)}>
              {EXPANSION_PHASE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Status">
            <select value={status} disabled={saving} onChange={(e) => setStatus(e.target.value)}>
              {EXPANSION_STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </FormField>
        </div>
        <CheckboxMultiSelect
          label="Departments"
          options={EXPANSION_DEPARTMENT_OPTIONS}
          value={departments}
          disabled={saving}
          required
          onChange={setDepartments}
        />
      </section>

      {error ? (
        <p className="project-detail-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="add-project-actions">
        <button type="submit" className="add-project-submit" disabled={saving}>
          {saving ? "Adding…" : "Add subsegment"}
        </button>
        <Link to={`/expansion/segment/${parentWorkOrder}`} className="project-detail-muted">
          Cancel
        </Link>
      </div>
    </form>
  );
}

function resolveCustomerFromExpansionRow(row) {
  if (!row) return "";
  const summary =
    row.acf_groups_json?.project_summary || row.acf_groups_json?.["Project Summary Test 2"] || {};
  for (const value of [row.customer, summary.customer_name, summary.customer]) {
    const text = value != null ? String(value).trim() : "";
    if (text) return text;
  }
  return "";
}

function resolveGroupCustomerFromRows(parentWo, rows) {
  for (const row of rows || []) {
    const customer = resolveCustomerFromExpansionRow(row);
    if (customer) return customer;
  }
  return "CVIN Expansion";
}

export default function AddExpansionSubsegmentPage({ apiUrl, getAuthHeaders }) {
  const { base } = useParams();
  const parentWorkOrder = String(base || "").trim();
  const viewerEmail = getViewerEmailFromStorage();
  const allowed = canAddExpansionSubsegment(viewerEmail);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [segmentRows, setSegmentRows] = useState([]);
  const [nextWorkOrderHint, setNextWorkOrderHint] = useState("");

  const segmentLabel = useMemo(
    () => segmentFromSubsegmentPrefix(segmentRows),
    [segmentRows]
  );
  const inheritedCustomer = useMemo(
    () => resolveGroupCustomerFromRows(parentWorkOrder, segmentRows),
    [parentWorkOrder, segmentRows]
  );

  const computeNextWorkOrderHint = useCallback((parentWo, matched) => {
    let maxSuffix = 0;
    for (const row of matched || []) {
      const wo = String(row.work_order || "").trim();
      if (!wo.startsWith(`${parentWo}.`)) continue;
      const suffix = parseInt(wo.slice(parentWo.length + 1), 10);
      if (Number.isFinite(suffix) && suffix > maxSuffix) maxSuffix = suffix;
    }
    const nextSuffix = String(maxSuffix + 1).padStart(2, "0");
    return `${parentWo}.${nextSuffix}`;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = apiUrl || getApiBase();
      const { data } = await axios.get(`${url}/projects`, { headers: getAuthHeaders() });
      const all = Array.isArray(data) ? data.filter(isCvinExpansion) : [];
      const matched = filterExpansionRowsForBase(all, parentWorkOrder);
      const hasSubsegment = matched.some((row) => {
        const wo = String(row.work_order || "").trim();
        return wo.includes(".");
      });
      if (!matched.length || !hasSubsegment) {
        setError(`No expansion segment found for work order ${parentWorkOrder}.`);
        setSegmentRows([]);
        return;
      }
      setSegmentRows(matched);
      setNextWorkOrderHint(computeNextWorkOrderHint(parentWorkOrder, matched));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Could not load expansion segment.");
      setSegmentRows([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, computeNextWorkOrderHint, getAuthHeaders, parentWorkOrder]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!allowed) {
    return (
      <div className="add-project-page">
        <div className="add-project-page-inner">
          <p className="project-detail-muted">
            Adding subsegments is limited to Coordinators, Permitting staff, and Administrators.
          </p>
          <Link to={`/expansion/segment/${parentWorkOrder}`}>
            Back to segment {parentWorkOrder}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="add-project-page">
        <div className="add-project-page-inner">
          <p className="project-detail-muted">Loading segment {parentWorkOrder}…</p>
        </div>
      </div>
    );
  }

  if (error || !segmentRows.length) {
    return (
      <div className="add-project-page">
        <div className="add-project-page-inner">
          <p className="project-detail-error">{error || "Expansion segment not found."}</p>
          <Link to="/">Back to board</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="add-project-page">
      <div className="add-project-page-inner">
        <Link to={`/expansion/segment/${parentWorkOrder}`} className="project-detail-back">
          ← Back to segment {parentWorkOrder}
        </Link>
        <h2 className="add-project-title">Add subsegment</h2>
        <p className="add-project-subtitle">
          Add another subsegment under parent work order {parentWorkOrder}
          {segmentLabel ? ` (segment ${segmentLabel})` : ""}.
        </p>
        <AddExpansionSubsegmentForm
          apiUrl={apiUrl}
          getAuthHeaders={getAuthHeaders}
          parentWorkOrder={parentWorkOrder}
          segmentLabel={segmentLabel}
          inheritedCustomer={inheritedCustomer}
          nextWorkOrderHint={nextWorkOrderHint}
        />
      </div>
    </div>
  );
}

export function AddExpansionSubsegmentGate(props) {
  return <AddExpansionSubsegmentPage {...props} />;
}
